import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, RootState } from "./store";
import apiClient from "../services/api_client";
import { createAsyncThunk } from "@reduxjs/toolkit";

export interface UserDTO {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string | null;
}

// Define proper error types
interface ApiError {
  message?: string;
  response?: {
    data?: {
      message?: string;
      detail?: string;
    };
  };
}

interface ApiResponse<T> {
  content?: T[];
  data?: T[];
  drones?: T[];
}

type DroneApiResponse = DroneDTO[] | ApiResponse<DroneDTO>;

// Remove or comment out the Spec interface if no longer needed elsewhere
// export interface Spec {
//   value: string;
//   description: string;
// }

export interface DroneDTO {
  id: number;
  name: string;
  price: number;
  editCost?: number;
  travelCost?: number;
  currency: "RON" | "EUR";
  location: string;
  lat: number;
  lng: number;
  dateTime: string; // Keep as string, formatted correctly before sending
  description: string;
  extraGears: string[];
  model: string;
  brand: string;
  extraDetails: string;
  imageKeys: string[]; // Primary field for image identifiers
  imageUrls?: string[]; // Keep for backward compatibility only
  imageFiles?: File[]; // For tracking File objects for upload, not sent to backend
  specs: { [key: string]: string }; // Changed from Spec[] to an object/dictionary type
  categories: string[]; // Changed from single category to array of categories
  postedBy: UserDTO;
}

interface DroneState {
  drones: DroneDTO[];
  selectedDrone: DroneDTO | null;
  loading: boolean;
  error: string | null;
}

const initialState: DroneState = {
  drones: [],
  selectedDrone: null,
  loading: false,
  error: null,
};

const droneSlice = createSlice({
  name: "drones",
  initialState,
  reducers: {
    setDrones: (state, action: PayloadAction<DroneDTO[]>) => {
      state.drones = action.payload;
    },
    appendDrones: (state, action: PayloadAction<DroneDTO[]>) => {
      state.drones = [...state.drones, ...action.payload];
    },
    setSelectedDrone: (state, action: PayloadAction<DroneDTO | null>) => {
      state.selectedDrone = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    addDrone: (state, action: PayloadAction<DroneDTO>) => {
      state.drones.push(action.payload);
    },
    updateDrone: (state, action: PayloadAction<DroneDTO>) => {
      const index = state.drones.findIndex(
        (drone) => drone.id === action.payload.id,
      );
      if (index !== -1) {
        state.drones[index] = action.payload;
      }
    },
    removeDrone: (state, action: PayloadAction<number>) => {
      state.drones = state.drones.filter(
        (drone) => drone.id !== action.payload,
      );
    },
  },
});

export const {
  setDrones,
  appendDrones,
  setSelectedDrone,
  setLoading,
  setError,
  addDrone,
  updateDrone,
  removeDrone,
} = droneSlice.actions;

// Thunks
export const fetchAllDrones = (
  page: number = 0,
  size: number = 12,
  append: boolean = false,
  name?: string,
  city?: string
) => async (dispatch: AppDispatch) => {
  dispatch(setLoading(true));
  let patchedDrones: DroneDTO[] = [];
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString()
    });
    
    if (name && name.trim()) {
      params.append('name', name.trim());
    }
    
    if (city && city.trim()) {
      params.append('city', city.trim());
    }
    
    const response = await apiClient.get<DroneApiResponse>(`/drones?${params.toString()}`);
    
    // Handle various response formats
    let drones: DroneDTO[] = [];
    if (Array.isArray(response)) {
      drones = response;
    } else if (response && typeof response === 'object') {
      // For Spring-style pagination: look for .content property
      if (Array.isArray(response.content)) {
        drones = response.content;
      } else if (Array.isArray(response.data)) {
        drones = response.data;
      } else if (response.drones && Array.isArray(response.drones)) {
        drones = response.drones;
      } else {
        // If we can't find an array, fallback to empty array
        drones = [];
      }
    }
    
    // Patch: Ensure every drone has a currency property
    patchedDrones = drones.map((drone) => ({
      ...drone,
      currency: drone.currency || "RON",
    }));

    if (append) {
      dispatch(appendDrones(patchedDrones));
    } else {
      dispatch(setDrones(patchedDrones));
    }
    dispatch(setError(null));
  } catch (error) {
    const apiError = error as ApiError;
    dispatch(setError(apiError.message || "Failed to fetch drones"));
  } finally {
    dispatch(setLoading(false));
  }
  
  // Return the fetched drones for pagination/end detection (moved outside finally)
  return patchedDrones;
};

export const fetchUserDrones = () => async (dispatch: AppDispatch) => {
  dispatch(setLoading(true));
  let patchedDrones: DroneDTO[] = [];
  try {
    const response = await apiClient.get<DroneApiResponse>('/drones/my-drones');
    
    // Handle various response formats
    let drones: DroneDTO[] = [];
    if (Array.isArray(response)) {
      drones = response;
    } else if (response && typeof response === 'object') {
      // For Spring-style pagination: look for .content property
      if (Array.isArray(response.content)) {
        drones = response.content;
      } else if (Array.isArray(response.data)) {
        drones = response.data;
      } else if (response.drones && Array.isArray(response.drones)) {
        drones = response.drones;
      } else {
        // If we can't find an array, fallback to empty array
        drones = [];
      }
    }
    
    // Patch: Ensure every drone has a currency property
    patchedDrones = drones.map((drone) => ({
      ...drone,
      currency: drone.currency || "RON",
    }));

    dispatch(setDrones(patchedDrones));
    dispatch(setError(null));
  } catch (error) {
    const apiError = error as ApiError;
    dispatch(setError(apiError.message || "Failed to fetch user drones"));
  } finally {
    dispatch(setLoading(false));
  }
  
  return patchedDrones;
};

export const fetchDroneById = (id: number) => async (dispatch: AppDispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await apiClient.get<DroneDTO>(`/drones/${id}`);
    // Patch: Ensure selected drone has a currency property
    const patchedDrone = { ...response, currency: response.currency || "RON" };
    dispatch(setSelectedDrone(patchedDrone));
    dispatch(setError(null));
  } catch (error) {
    const apiError = error as ApiError;
    dispatch(setError(apiError.message || "Failed to fetch drone"));
  } finally {
    dispatch(setLoading(false));
  }
};

export const createDrone = createAsyncThunk<
  DroneDTO,
  Omit<DroneDTO, "id" | "postedBy">,
  { state: RootState; rejectValue: { message: string } }
>(
  "drones/createDrone",
  async (droneData, { getState, rejectWithValue, dispatch }) => {
    dispatch(setLoading(true));
    try {
      const state = getState();
      const user = state.auth.user;

      if (!user) {
        throw new Error("User not authenticated.");
      }

      const postedBy: UserDTO = {
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phoneNumber: user.phoneNumber,
      };

      // Create a FormData object for multipart/form-data request
      const formData = new FormData();

      // Prepare drone data without images
      const { imageFiles, ...droneDataWithoutFiles } = droneData;
      const droneDataToSend = {
        ...droneDataWithoutFiles,
        imageKeys: droneData.imageKeys || [],
        postedBy,
      };

      // Add drone data as a JSON string/blob
      const droneDataBlob = new Blob(
        [JSON.stringify(droneDataToSend)],
        { type: "application/json" }
      );
      formData.append("droneData", droneDataBlob);

      // Add each image file separately - directly use imageFiles array
      if (imageFiles && imageFiles.length > 0) {
        imageFiles.forEach((file) => {
          if (file instanceof File) {
            formData.append("images", file);
          }
        });
      }

      // Send the FormData - Content-Type will be set automatically by the browser
      const response = await apiClient.post<DroneDTO>("/drones", formData);

      dispatch(addDrone(response));
      dispatch(setError(null));
      dispatch(setLoading(false));
      return response;
    } catch (error) {
      dispatch(setLoading(false));
      const apiError = error as ApiError;
      const payload = apiError?.response?.data || {};
      const message = payload.message || apiError?.message || "Failed to create drone. Please check your data.";
      
      dispatch(setError(message));
      return rejectWithValue({ message });
    }
  }
);

export const updateDroneById = createAsyncThunk<
  DroneDTO,
  { id: number; droneData: Partial<Omit<DroneDTO, "postedBy">> },
  { state: RootState; rejectValue: { message: string } }
>(
  "drones/updateDroneById",
  async ({ id, droneData }, { getState, rejectWithValue, dispatch }) => {
    dispatch(setLoading(true));
    try {
      const state = getState();
      const user = state.auth.user;

      if (!user) {
        throw new Error("User not authenticated.");
      }

      const postedBy: UserDTO = {
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phoneNumber: user.phoneNumber,
      };

      // Create a FormData object for multipart/form-data request
      const formData = new FormData();

      // Prepare drone data without images
      const { imageFiles, ...droneDataWithoutFiles } = droneData;
      const droneDataToSend = {
        ...droneDataWithoutFiles,
        imageKeys: droneData.imageKeys || [],
        postedBy,
      };

      // Add drone data as a JSON string/blob
      const droneDataBlob = new Blob(
        [JSON.stringify(droneDataToSend)],
        { type: "application/json" }
      );
      formData.append("droneData", droneDataBlob);

      // Add each image file separately - directly use imageFiles array
      if (imageFiles && imageFiles.length > 0) {
        imageFiles.forEach((file) => {
          if (file instanceof File) {
            formData.append("images", file);
          }
        });
      }

      // Send the FormData - Content-Type will be set automatically by the browser
      const response = await apiClient.put<DroneDTO>(`/drones/${id}`, formData);

      dispatch(updateDrone(response));
      dispatch(setError(null));
      dispatch(setLoading(false));
      return response;
    } catch (error) {
      dispatch(setLoading(false));
      const apiError = error as ApiError;
      const payload = apiError?.response?.data || {};
      const message = payload.message || apiError?.message || "Failed to update drone. Please check your data.";
      
      dispatch(setError(message));
      return rejectWithValue({ message });
    }
  }
);

export const deleteDroneById = (id: number) => async (dispatch: AppDispatch) => {
  dispatch(setLoading(true));
  try {
    await apiClient.delete(`/drones/${id}`);
    dispatch(removeDrone(id)); 
    dispatch(setError(null));
  } catch (error) {
    const apiError = error as ApiError;
    const errorMessage =
      apiError?.response?.data?.detail ||
      apiError?.response?.data?.message ||
      apiError?.message ||
      "Failed to delete drone";
    dispatch(setError(errorMessage));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Selectors
export const selectAllDrones = (state: RootState) => state.drones.drones;
export const selectSelectedDrone = (state: RootState) =>
  state.drones.selectedDrone;
export const selectDronesLoading = (state: RootState) => state.drones.loading;
export const selectDronesError = (state: RootState) => state.drones.error;

// New selector for user's drones
export const selectUserDrones = (userId: number) => (state: RootState) => 
  state.drones.drones.filter(drone => drone.postedBy?.id === userId);

// Function to fetch signed URL for image key
export const fetchSignedImageUrl = async (key: string): Promise<string> => {
  try {
    // Ensure key is properly encoded
    const encodedKey = encodeURIComponent(key);
    
    const apiUrl = `/file/signed-url?key=${encodedKey}`;
    
    const response = await apiClient.get<{ url?: string, signedUrl?: string }>(apiUrl);
    
    // Return the URL from the response based on its type
    if (typeof response === 'string') {
      return response;
    } else if (response && typeof response === 'object') {
      // Check for signedUrl property (what the API is actually returning)
      if (response.signedUrl) {
        return response.signedUrl;
      }
      // Also check for url property as a fallback
      if (response.url) {
        return response.url;
      }
    }
    
    return '';
  } catch {
    return ''; // Return empty string on error
  }
};

export default droneSlice.reducer;
